import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import defaultTo from 'lodash/defaultTo';
import { isJSMProduct } from '@atlassian/cssxp-utils-product';
import type { State } from '../types';
import { stitchChangeKeyObjects } from '../utils';
import type {
  OrderSummaryLineItem,
  OrderSummaryLineItems,
  OrderSummaryAppLineItem,
} from '../../ui/order-summary/src/components/types';
import { getTrialStatusForEntitlement } from '../utils/get-trial-status-for-entitlement';
import { Product, cartLinkedEntitlement } from '../../ui/product-line-items/src/types';
import { LinkedEntitlementRelationShipType, OperationType } from '../../types';
import { sortLinkedEntitlements } from '../utils/sort-linked-entitlements';

const sortLineItems = (a: Product | cartLinkedEntitlement, b: Product | cartLinkedEntitlement) => {
  const firstName = defaultTo(a?.name, a?.name ?? '');
  const secondName = defaultTo(b?.name, b?.name ?? '');
  return firstName.localeCompare(secondName);
};

export function getProductAndAppsLineItems(state: State) {
  const orderSummaryLineItems: OrderSummaryLineItems = [];
  const selectedProductsInitialDetails = Object.values(state.products);
  const selectedProducts = Object.values(stitchChangeKeyObjects(state.products, state.selectedProducts));
  const sortedProducts = selectedProducts?.sort(sortLineItems);
  const containerProducts = sortedProducts.filter((product) => !!product.containerId);
  const productsGroupedByContainer = groupBy(containerProducts, 'containerId');

  const idsOfLastProductInContainers = Object.keys(productsGroupedByContainer).map((containerId) => {
    const products = productsGroupedByContainer[containerId];
    return products[products?.length - 1].id;
  });

  sortedProducts.forEach((product) => {
    // Evaluating Offering name
    const productOfferings = state.offeringsAndpricingPlans?.[product.productId];
    let productOfferingDetails = {};
    let offeringLevel = 0;
    productOfferings?.forEach((productOffering) => {
      if (productOffering.id === product.offeringId) {
        productOfferingDetails = productOffering;
        offeringLevel = productOffering?.level;
      }
    });
    // @ts-ignore
    const productOfferingName = productOfferingDetails?.offeringName;

    // Evaluating initial offering name
    const productWithInitialDetails = selectedProductsInitialDetails.find(
      (initialProduct) => initialProduct.id === product.id
    );
    let initialOfferingName = '';
    let initialOfferingLevel = 0;

    if (productWithInitialDetails) {
      const initialProductOfferings = state.offeringsAndpricingPlans?.[productWithInitialDetails.productId];
      let initialProductOfferingDetails = {};
      initialProductOfferings?.forEach((initialProductOffering) => {
        if (initialProductOffering.id === productWithInitialDetails.offeringId) {
          initialProductOfferingDetails = initialProductOffering;
          initialOfferingLevel = initialProductOffering?.level;
        }
      });
      // @ts-ignore
      initialOfferingName = initialProductOfferingDetails?.offeringName;
    }

    // Evaluating List Price of line item
    const productEstimates =
      state.billEstimate && state.billEstimate.estimates ? state.billEstimate.estimates[product.id] : null;
    const productListPrice = productEstimates?.listPrice || 0;

    // we have to show the partner discount percent for the products even if we don't get from next bill estimate
    const recurringDiscountPercent = get(state, `recurringBillEstimate.estimates.${product.id}.discountPercent`, null);

    // Evaluating Total discount for line item
    const totalDiscount = productEstimates?.totalDiscount || 0;

    // Evaluating estimate including partner discount for line item
    const discountPercent = productEstimates?.discountPercent || recurringDiscountPercent || 0;
    const estimateIncludingPartnerDiscount = productEstimates?.estimateIncludingPartnerDiscount || 0;

    const isAdminFeeApplicable = productEstimates?.isAdminFeeApplicable || false;
    const adminFee = productEstimates?.adminFee || 0;
    const itemLevelTaxInfo = productEstimates?.itemLevelTaxInfo || [];

    const grandTotal = productEstimates?.grandTotal || 0;

    // Evaluating if line item is container product or standalone
    const productContainerId = product?.containerId || '';
    const isItAJiraProduct = !!productContainerId;

    const isItADowngrade = initialOfferingLevel > offeringLevel;
    const isItAUpgrade = initialOfferingLevel < offeringLevel;
    // Evaluating new plan start date
    const newPlanStartDate = productWithInitialDetails?.nextBillDate;

    let totalPriceForRelatedApps = 0;

    const getBillEstimateForApps = (
      productAppLineItems: OrderSummaryAppLineItem[],
      selectedLinkedEntitlements: State['selectedLinkedEntitlements'],
      offeringsAndPricingPlans?: State['offeringsAndpricingPlans']
    ) => {
      totalPriceForRelatedApps = productAppLineItems?.reduce<number>((acc, appLineItem) => {
        return (
          acc +
          (state.billEstimate && state.billEstimate.estimates
            ? state.billEstimate.estimates[appLineItem.id]?.grandTotal
            : 0)
        );
      }, 0);
      return productAppLineItems?.map((appLineItem) => {
        const selectedAppLineItem = selectedLinkedEntitlements?.[appLineItem.id] ?? {};
        const offeringAndPricingPlansForApp = offeringsAndPricingPlans?.[appLineItem.productId];
        const appEstimates =
          state.billEstimate && state.billEstimate.estimates ? state.billEstimate.estimates[appLineItem.id] : null;

        // we have to show the partner discount percent for the apps even if we don't get from next bill estimate
        const appsRecurringDiscountPercent = get(
          state,
          `recurringBillEstimate.estimates.${appLineItem.id}.discountPercent`,
          null
        );
        const appDiscountPercent = appEstimates?.discountPercent || appsRecurringDiscountPercent || 0;

        // this is required to display actual estimated users in the order summary where we send for order placement
        const actualUsersForAppOrderPlacement = (() => {
          // we have to show active users as 1 for new app
          if (appLineItem.operationType === OperationType.NEW) {
            return '1';
          }
          return appLineItem.activeUsers;
        })();

        const offeringName =
          offeringAndPricingPlansForApp?.find((offering: any) => offering?.id === selectedAppLineItem?.offeringId)
            ?.offeringName ?? appLineItem?.offeringName;

        return {
          ...appLineItem,
          ...appEstimates,
          ...selectedAppLineItem,
          discountPercent: appDiscountPercent,
          activeUsers: actualUsersForAppOrderPlacement,
          offeringName: offeringName,
        };
      });
    };

    const getAppLineItems = () => {
      const offeringsAndPricingPlans = state.offeringsAndpricingPlans;
      if (!isItAJiraProduct) {
        const productAppLineItems = state.linkedEntitlements[product.id] || [];

        return getBillEstimateForApps(productAppLineItems, state.selectedLinkedEntitlements, offeringsAndPricingPlans);
      }

      const isLastProductInContainer = idsOfLastProductInContainers.includes(product.id);
      if (isLastProductInContainer) {
        const hasJSMProduct = productsGroupedByContainer[productContainerId].some((product) =>
          isJSMProduct(product.productId)
        );
        const linkedEntitlementsForContainer = state.linkedEntitlements[productContainerId] || [];
        const productAppLineItems = linkedEntitlementsForContainer?.filter((appLineItem) => {
          // if we don't have a JSM product in selected cart, then we don't show add-ons in the order summary
          return !(!hasJSMProduct && appLineItem.relationshipType === LinkedEntitlementRelationShipType.ADD_ON);
        });
        // sorting linked entitlements by relationshipType and name
        const sortedLinkedEntitlements = sortLinkedEntitlements(productAppLineItems);
        return getBillEstimateForApps(
          sortedLinkedEntitlements,
          state.selectedLinkedEntitlements,
          offeringsAndPricingPlans
        );
      }
      return [];
    };

    // this is required to display actual estimated users in the order summary where we send for order placement
    const actualEstimatedUsersForOrderPlacement = (() => {
      // if the product type is transfer or own, we should show the active users as initial active users
      if (product.operationType === OperationType.TRANSFER || product.operationType === OperationType.OWN) {
        return Number(state.products?.[product.id]?.activeUsers);
      }
      // we should show active users as 1 for new product
      if (product.operationType === OperationType.NEW) {
        return 1;
      }
      return Number(product.activeUsers);
    })();

    const appLineItems: OrderSummaryAppLineItem[] = getAppLineItems();

    const inTrial = (() => {
      // checking if initial product details are available for line item
      if (productWithInitialDetails) {
        return getTrialStatusForEntitlement({
          currentOfferingId: productWithInitialDetails?.offeringId,
          updatedOfferingId: product.offeringId,
          productId: product.productId,
          inCurrentlyInTrial: !!productWithInitialDetails?.inTrial,
          offeringsAndpricingPlans: state.offeringsAndpricingPlans || {},
        });
      }
      return product?.inTrial;
    })();

    const orderSummaryLineItem: OrderSummaryLineItem = {
      logoId: product.productId,
      name: product.name,
      entitlementNumber: product.entitlementNumber,
      estimatedNumberOfUsers: actualEstimatedUsersForOrderPlacement,
      billingCycleLabel: product.billingCycle,
      nextBillDate: product.nextBillDate,
      offeringName: productOfferingName,
      existingCloudSite: product.cloudSite,
      listPrice: productListPrice,
      grandTotal: grandTotal,
      isItAJiraProduct: isItAJiraProduct,
      discountPercent: discountPercent,
      estimateIncludingPartnerDiscount: estimateIncludingPartnerDiscount,
      totalDiscount: totalDiscount,
      isAdminFeeApplicable: isAdminFeeApplicable,
      itemLevelTaxInfo: itemLevelTaxInfo,
      appLineItems: appLineItems,
      isFreeProduct: false,
      adminFee: adminFee,
      totalPriceOfApps: totalPriceForRelatedApps,
      productKey: product.productId,
      lineItemId: product.productId,
      id: product.id,
      productStatuses: product?.productStatuses,
      trialLengthInDays: product?.trialLengthInDays,
      inTrial,
      initialOfferingName: initialOfferingName,
      isItADowngrade: isItADowngrade,
      isItAUpgrade: isItAUpgrade,
      newPlanStartDate: newPlanStartDate,
      organisationId: product?.organizationId,
      organisationName: product?.organisationName,
      isSandbox: product?.isSandbox,
      isOnUpgradeTrial: product?.isOnUpgradeTrial,
      isOrgProduct: product?.isOrgProduct,
      parentEntitlementId: product?.parentEntitlementId,
    };
    orderSummaryLineItems.push(orderSummaryLineItem);
  });

  return orderSummaryLineItems;
}
